import type { CalcResult, GrowthJudgement, PlanInputs } from '../types';
import { fmtCeil, fmtPct } from '../lib/format';
import { NumberField } from './NumberField';

interface Props {
  inputs: PlanInputs;
  result: CalcResult;
  onChange: (patch: Partial<PlanInputs>) => void;
  onAutoFill: () => void;
  onReset: () => void;
}

const judgeClass = (j: GrowthJudgement): string => {
  if (j === 'OK') return 'auto ok';
  if (j === 'NG') return 'auto ng';
  return 'auto';
};

export function PlanTable({ inputs, result, onChange, onAutoFill, onReset }: Props) {
  return (
    <div className="section">
      <h2>
        <span className="num">4</span>計画数値入力（自動計算 / 手動上書き）
      </h2>
      <div className="btn-row">
        <button className="btn btn-purple" onClick={onAutoFill}>
          <i className="fas fa-magic" /> 推奨レンジで自動算出
        </button>
        <button className="btn btn-gray" onClick={onReset}>
          <i className="fas fa-undo" /> リセット
        </button>
      </div>
      <p className="hint">
        「推奨レンジで自動算出」を押すと、区分ごとの推奨レンジ（年ごとに下限〜上限の幅）で3年間の計画値が自動入力されます。手動で上書きも可能です。1人当たり給与は<b>切り上げ</b>で算出されます。
      </p>
      <div className="table-wrap">
        <table className="plan-table">
          <thead>
            <tr>
              <th />
              <th className="head-prev">前年度実績値</th>
              <th className="head-plan">計画数値（1年度目）</th>
              <th className="head-plan">計画数値（2年度目）</th>
              <th className="head-plan">計画数値（3年度目）</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="lbl-cell">給与支給総額（円）</td>
              <td>
                <NumberField value={inputs.prevSalary} step={10000} readOnly className="ro" />
              </td>
              <td>
                <NumberField value={inputs.salary1} step={10000} min={0} onChange={(n) => onChange({ salary1: n })} />
              </td>
              <td>
                <NumberField value={inputs.salary2} step={10000} min={0} onChange={(n) => onChange({ salary2: n })} />
              </td>
              <td>
                <NumberField value={inputs.salary3} step={10000} min={0} onChange={(n) => onChange({ salary3: n })} />
              </td>
            </tr>
            <tr>
              <td className="lbl-cell">従業員数（人）</td>
              <td>
                <NumberField value={inputs.prevEmployees} integer readOnly className="ro" />
              </td>
              <td>
                <NumberField value={inputs.emp1} integer min={0} onChange={(n) => onChange({ emp1: n })} />
              </td>
              <td>
                <NumberField value={inputs.emp2} integer min={0} onChange={(n) => onChange({ emp2: n })} />
              </td>
              <td>
                <NumberField value={inputs.emp3} integer min={0} onChange={(n) => onChange({ emp3: n })} />
              </td>
            </tr>
            <tr>
              <td className="lbl-cell">1人当たり給与支給総額（円・切上）</td>
              <td className="auto">{fmtCeil(result.perEmp[0])}</td>
              <td className="auto">{fmtCeil(result.perEmp[1])}</td>
              <td className="auto">{fmtCeil(result.perEmp[2])}</td>
              <td className="auto">{fmtCeil(result.perEmp[3])}</td>
            </tr>
            <tr>
              <td className="lbl-cell">年平均成長率（%）</td>
              <td className="auto" style={{ background: 'var(--g50)' }}>—</td>
              <td className={judgeClass(result.yearJudge[0])}>{fmtPct(result.cagr1)}</td>
              <td className={judgeClass(result.yearJudge[1])}>{fmtPct(result.cagr2)}</td>
              <td className={judgeClass(result.yearJudge[2])}>{fmtPct(result.cagr3)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
